import { BulkOrderStatusEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { ColumnDef } from "@tanstack/react-table";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  ParamDataTable,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import {
  exportBulkOrders,
  useGetBulkOrders,
  getGetBulkOrdersQueryOptions,
} from "@/requests/gen/react-query/bulk-order";
import formatUSD from "@/lib/format-currency";
import { format, parseISO } from "date-fns";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { FlaskConical, Plus } from "lucide-react";
import {
  createLoader,
  parseAsArrayOf,
  parseAsBoolean,
  parseAsStringEnum,
  useQueryStates,
  type inferParserType,
} from "nuqs";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/ui/toggle";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { keepPreviousData } from "@tanstack/react-query";
import { Header } from "@/components/universal/header";
import { ExportCSV } from "@/components/ui/csv-export";

const bulkOrderSearchParsers = {
  statuses: parseAsArrayOf(
    parseAsStringEnum(Object.values(BulkOrderStatusEnum)),
  ),
  is_sample: parseAsBoolean,
};

const bulkOrderSearchAllParsers = {
  ...dataTableSearchParsers,
  ...bulkOrderSearchParsers,
};

const loadBulkOrderSearch = createLoader(bulkOrderSearchAllParsers);

type BulkOrderSearch = inferParserType<typeof bulkOrderSearchAllParsers>;

function bulkOrderQueryParams(
  p: BulkOrderSearch,
): Parameters<typeof getGetBulkOrdersQueryOptions>[0] {
  return {
    page: p.page,
    limit: p.limit,
    search: p.search,
    sort_column: p.sort_column,
    ascending: p.ascending,
    created_at_start: p.created_at_start,
    created_at_end: p.created_at_end,
    bulk_order_statuses: p.statuses,
    is_sample: p.is_sample,
  };
}

export const Route = createFileRoute(
  "/_private/_practice-360/bulk-order/lookup",
)({
  loader: ({ context: { queryClient }, location }) =>
    queryClient.prefetchQuery(
      getGetBulkOrdersQueryOptions(
        bulkOrderQueryParams(loadBulkOrderSearch(location.searchStr)),
      ),
    ),
  component: BulkOrderLookupPage,
});

// Define the columns for the table
interface BulkOrderTableProps {
  link?: LinkProps;
  bulk_order_id: string;
  confirmed_at: string;
  total: string;
  status: BulkOrderStatusEnum | "";
  entity: string;
  is_sample: boolean;
}

// Define the columns headers and accessor keys
const columns: ColumnDef<BulkOrderTableProps>[] = [
  {
    header: "Bulk Order ID",
    accessorKey: "bulk_order_id",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <span>{row.original.bulk_order_id}</span>
        {row.original.is_sample && <Badge variant="secondary">Sample</Badge>}
      </div>
    ),
  },
  {
    header: "Confirmed At",
    accessorKey: "confirmed_at",
    cell: (value) => {
      const dateString = value.getValue() as string;
      if (!dateString) {
        return "";
      }
      return format(parseISO(dateString), "MM-dd-yyyy");
    },
    enableSorting: true,
  },
  { header: "Total", accessorKey: "total" },
  { header: "Status", accessorKey: "status" },
  { header: "Entity", accessorKey: "entity" },
];

// Main component
function BulkOrderLookupPage() {
  const [queryParams, setQueryParams] = useQueryStates(
    bulkOrderSearchAllParsers,
  );

  // Get the bulk_orders
  const bulk_orders = useGetBulkOrders(bulkOrderQueryParams(queryParams), {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  // Map the data to the table format
  const tableData: BulkOrderTableProps[] | undefined =
    bulk_orders.data?.items.map((bulk_order) => {
      return {
        link: linkOptions({
          to: "/bulk-order/$bulkOrderId",
          params: { bulkOrderId: String(bulk_order.bulk_order_id) },
        }),
        bulk_order_id: bulk_order.bulk_order_id,
        confirmed_at: bulk_order.confirmed_at ?? "",
        total: formatUSD(bulk_order.total_price),
        status: bulk_order.status,
        entity: bulk_order.entity.name,
        is_sample: bulk_order.is_sample,
      };
    });

  return (
    <div className="text-left">
      <title>Bulk Orders - Legrande Health Portal</title>
      <Header
        crumbs={[{ name: "Bulk Orders", link: { to: "/bulk-order/lookup" } }]}
      />
      <Card>
        <CardHeader>
          <CardTitle>Bulk Orders</CardTitle>
          <CardDescription>
            View recent bulk orders below. You can search, filter, and click to
            see more details.
          </CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/bulk-order/create">
                <Plus />
                Create Bulk Order
              </Link>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent>
          <ParamDataTableToolbar>
            <ParamSearchFilter isFetching={bulk_orders.isFetching} />
            <DropdownFilter
              title="Status"
              options={[
                {
                  items: Object.values(BulkOrderStatusEnum)
                    .filter((status) => status !== BulkOrderStatusEnum.Reorder)
                    .map((status) => {
                      return { label: status, value: status };
                    }),
                },
              ]}
              values={queryParams.statuses || []}
              onChange={(values) => setQueryParams({ statuses: values })}
            />
            <Toggle
              variant="outline"
              pressed={queryParams.is_sample === true}
              onPressedChange={(pressed) =>
                setQueryParams({ is_sample: pressed ? true : null })
              }
            >
              <FlaskConical />
              Samples
            </Toggle>
            <ParamClearButton />
          </ParamDataTableToolbar>
          <ParamDataTable
            columns={columns}
            data={tableData ?? []}
            isLoading={bulk_orders.isLoading}
            isError={bulk_orders.isError}
            count={bulk_orders.data?.count}
            footerActions={
              <ExportCSV
                onExport={() =>
                  exportBulkOrders({
                    search: queryParams.search,
                    sort_column: queryParams.sort_column,
                    ascending: queryParams.ascending,
                    created_at_start: queryParams.created_at_start,
                    created_at_end: queryParams.created_at_end,
                    bulk_order_statuses: queryParams.statuses,
                    hidden_columns: queryParams.hidden,
                  })
                }
                filename="bulk-orders"
                requiresPHIWarning={false}
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
